import { useEffect, useRef, useState } from 'react';
import {
  PhotoTypeNotAllowedError,
  WebcamNotFoundError,
} from '../../helpers/definedErrors';
import { Button, Flex, Alert, Text, Link } from '@smarterservices/smarter-ui';
import idFrame from '../../assets/idFrame.png';
import selfieFrame from '../../assets/selfieFrame.png';
import Compressor from 'compressorjs';
import { uploadSessionPhoto } from './api';
import { usePaneViewer } from '../PaneViewer';
import './index.css';
const PhotoUploader = ({ photoType, setIsTaskComplete, stepData }) => {
  const videoRef = useRef(null);
  const photoRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const { onNext } = usePaneViewer();
  useEffect(() => {
    if (!(photoType && (photoType === 'headshot' || photoType === 'idFront'))) {
      throw new PhotoTypeNotAllowedError();
    }
    handleSelectedVideoDevice();
  }, [photoType]);

  const handleSelectedVideoDevice = async () => {
    try {
      // Get the video device selected from ShareWebcam
      const selectedDeviceLabel = localStorage.getItem('spSelectedWebcamLabel');
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === 'videoinput');
      const selectedCamera = cameras.find(
        (camera) => camera.label === selectedDeviceLabel
      );
      if (selectedCamera) {
        const userStream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: selectedCamera.deviceId },
          },
          audio: false,
        });
        if (videoRef.current) {
          videoRef.current.srcObject = userStream;
        }
      } else {
        throw new WebcamNotFoundError();
      }
    } catch (error) {
      console.log('Error fetching video devices:', error);
    }
  };

  const dataURLtoFile = (dataUrl, fileName) => {
    let arr = dataUrl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  const handleCapturePhoto = async () => {
    if (capturedImage) {
      setIsTaskComplete(false);
      // We need to remove the captured image to allow a redo
      setCapturedImage(null);
      // Clear the error state when retaking photo
      setSubmitError(null);
      // remove the canvas
      if (photoRef.current) {
        const canvas = photoRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.style.display = 'none';
      }
    } else {
      const video = videoRef.current;
      const canvas = photoRef.current;

      // Set canvas dimensions to match video's actual dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const photo = new Image();
      photo.src = canvas.toDataURL('image/jpeg');
      photo.type = photoType;

      let photoFile = dataURLtoFile(photo.src, 'student_ID_verification');

      new Compressor(photoFile, {
        quality: 0.6,
        maxWidth: 246,
        maxHeight: 328,
        convertTypes: 'image/jpeg',
        convertSize: 5000,
        success(result) {
          let blob = result;
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = function () {
            const compressedPhoto = new Image();
            compressedPhoto.type = photoType;
            compressedPhoto.src = reader.result;
            setCapturedImage(compressedPhoto);
          };
        },
      });
    }
  };
  const handleSubmitPhoto = async () => {
    const result = await uploadSessionPhoto(capturedImage);
    if (result?.sessionPhotos) {
      setIsTaskComplete(true);
      setSubmitError(false);
      onNext();
    } else {
      setSubmitError(true);
    }
  };
  return (
    <Flex direction="column" alignItems="center" width="100%">
      <Flex direction="row" alignItems="center">
        <div className="video-capture">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="video-element"
            style={{
              display: capturedImage ? 'none' : 'block',
              width: '100%',
              maxHeight: '40vh',
              objectFit: 'contain',
              borderRadius: '4px',
              border: `1px solid #ccc`,
              margin: '0',
              boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0.1875rem 0.375rem, rgba(0, 0, 0, 0.16) 0px 0.1875rem 0.375rem',
          }}
          />
          <canvas
            ref={photoRef}
            className="canvas-element"
            style={{
              display: capturedImage ? 'block' : 'none',
              width: '100%',
              maxHeight: '40vh',
              objectFit: 'contain',
              borderRadius: '4px',
              border: `1px solid #ccc`,
              margin: '0',
              boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0.1875rem 0.375rem, rgba(0, 0, 0, 0.16) 0px 0.1875rem 0.375rem',
            }}
          />
          {/* Video Overlay Images */}
          {photoType === 'idFront' && (
            <img alt="ID Frame" src={idFrame} className="id-overlay" />
          )}
          {photoType === 'headshot' && (
            <img
              alt="Selfie Frame"
              src={selfieFrame}
              className="selfie-overlay"
            />
          )}
        </div>
      </Flex>
      <Flex>
        {submitError === null ? (
          <>
          {!capturedImage &&
            <Flex direction="row" alignItems="center" margin="small">
              <Button
                color="primary"
                size="medium"
                onClick={handleCapturePhoto}
                margin="medium 0 0 0"
              >
                {capturedImage ? stepData.retakeButton : stepData.captureButton}
              </Button>
            </Flex>
            }
            {capturedImage &&
            <Flex direction="column" alignItems="center" margin="small">
              <Alert variant="info">{stepData.confirmationMessage || "Does the image look ok?"}</Alert>
              <Button
                color="primary"
                size="medium"
                onClick={handleSubmitPhoto}
                margin="medium 0 small 0"
              >
                {stepData.submitButton}
              </Button>
              <Text as="div" size='small'><Link onClick={handleCapturePhoto}>{stepData.retakeMessage || "No, I need to retake the photo"}</Link></Text>
            </Flex>
            }
          </>
        ) : submitError ? (
          <>
            <Flex direction="column" alignItems="center">
              <Alert margin="small 0 0 0" variant="error">
                {stepData.failureMessage}
              </Alert>
              <Flex>
                {!capturedImage &&
                <Flex direction="row" alignItems="center">
                  <Button
                    color="primary"
                    size="medium"
                    onClick={handleCapturePhoto}
                    margin="medium 0 0 0"
                  >
                    {capturedImage
                      ? stepData.retakeButton
                      : stepData.captureButton}
                  </Button>
                </Flex>
                }
                {capturedImage &&
                <Flex direction="row" alignItems="center">
                  <Button
                      color="primary"
                      size="medium"
                      onClick={handleSubmitPhoto}
                      margin="medium 0 0 0"
                      interaction={capturedImage ? 'enabled' : 'disabled'}
                  >
                    {stepData.submitButton}
                  </Button>
                </Flex>
                }
              </Flex>
            </Flex>
          </>
        ) : (
          <Alert variant="success">{stepData.successMessage}</Alert>
        )}
      </Flex>
    </Flex>
  );
};

export default PhotoUploader;
